import * as React from 'react'

import {
  Box,
  Button,
  Typography,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material'

import { useDispatch, useSelector } from 'react-redux';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { CTextField } from '../../components/common/TextField';
import { CActionButton, CPageButton, CPageArrowButton } from '../../components/common/Button'
import MemberEmbeddingModal from '../../modals/admin/MemberEmbeddingModal';

import { 
  getDimVectors, 
  deleteDimVector 
} from '../../slices/dimVector';

function MemberEmbeddings() {
  const [openModal, setOpenModal] = React.useState(false);
  const [mode, setMode] = React.useState("Create");
  const [selectedEmbedding, setSelectedEmbedding] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  
  const dispatch = useDispatch();
  const { dimVectors, isLoading, pagination } = useSelector(state => state.dimVector);

  React.useEffect(() => {
    loadEmbeddings();
  }, [dispatch, currentPage]);

  const loadEmbeddings = () => {
    dispatch(getDimVectors({ 
      page: currentPage, 
      pageSize: 10 
    }));
  };

  const handleOpenNewEmbedding = () => {
    setSelectedEmbedding(null);
    setMode("Create");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    loadEmbeddings(); // Refresh the list
  };

  const handleEdit = (embedding) => {
    setSelectedEmbedding(embedding);
    setMode("Edit");
    setOpenModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Do you really want to remove this member embedding?")) {
      dispatch(deleteDimVector(id))
        .then(() => {
          loadEmbeddings(); // Refresh the list
        })
        .catch(err => console.log(err));
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const filteredEmbeddings = dimVectors.filter(embedding => 
    embedding.keyword?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    embedding.cube_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    embedding.dimension?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    embedding.member_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box className='w-full flex flex-column flex-basis flex-gap'>
      <Box sx={{ paddingTop: 2, paddingLeft: 4 }}>
        <Typography 
          sx={{ 
            fontSize: 24, 
            fontWeight: 600, 
            color: '#4A85F6'
          }}
        >
          Member Embeddings
        </Typography>
      </Box>
      
      <Box sx={{ paddingTop: 2, gap: 2 }} className='flex flex-row align-center'>
        <CTextField
          label=""
          id="search-text-field"
          placeholder='Search embeddings...'
          sx={{ m: 1, flex: 1, background: 'white' }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchOutlinedIcon /></InputAdornment>,
          }}
        />
        <Button
          sx={{
            textTransform: 'none',
            height: 54,
            borderRadius: 2
          }}
          endIcon={<AddIcon />}
          variant='contained'
          onClick={handleOpenNewEmbedding}
        >
          Add New Embedding
        </Button>
      </Box>

      <Box className='flex-basis'>
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Keyword
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Cube
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Dimension
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Member
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Created
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredEmbeddings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No embeddings found matching your search.' : 'No member embeddings found. Create your first embedding to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmbeddings.map((embedding) => (
                  <TableRow key={embedding.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {embedding.keyword || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={embedding.cube_name || 'N/A'} 
                        size="small" 
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {embedding.dimension || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {embedding.member_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {embedding.created_at ? new Date(embedding.created_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit">
                          <CActionButton
                            onClick={() => handleEdit(embedding)}
                            startIcon={<BorderColorIcon />}
                            sx={{ 
                              color: '#1976d2',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                              }
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Delete">
                          <CActionButton
                            onClick={() => handleDelete(embedding.id)}
                            startIcon={<DeleteOutlineIcon />}
                            sx={{ 
                              color: '#d32f2f',
                              '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                              }
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
            <CPageArrowButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              icon={<ArrowBackIcon />}
            />
            
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <CPageButton
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  active={currentPage === pageNum}
                >
                  {pageNum}
                </CPageButton>
              );
            })}
            
            <CPageArrowButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
              icon={<ArrowForwardIcon />}
            />
          </Box>
        )}
      </Box>

      {/* Modal */}
      <MemberEmbeddingModal
        open={openModal}
        mode={mode}
        data={selectedEmbedding}
        handleClose={handleCloseModal}
        onSuccess={loadEmbeddings}
      />
    </Box>
  );
}

export default MemberEmbeddings;